import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Eye, SendHorizonal, Check, X, Pause, RefreshCw, FileText, Bell } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  pending: 'In attesa', quoted: 'Preventivato', confirmed: 'Confermato',
  rejected: 'Rifiutato', paused: 'In pausa', expired: 'Scaduto',
};
const STATUS_COLORS = {
  pending: 'bg-yellow-900/30 text-yellow-400',
  quoted: 'bg-blue-900/30 text-blue-400',
  confirmed: 'bg-green-900/30 text-green-400',
  rejected: 'bg-red-900/30 text-red-400',
  paused: 'bg-gray-800 text-gray-500',
  expired: 'bg-[var(--lux-surface-3)] text-[var(--lux-border-4)]',
};

export default function BookingRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedReq, setSelectedReq] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/booking-requests?status=${filterStatus}`);
      setRequests(res.data);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (req) => {
    // Auto-fill supplier_quoted_price from supplier_submitted_price if not already set
    const enriched = {
      ...req,
      services_detail: (req.services_detail || []).map(s => ({
        ...s,
        supplier_quoted_price: (s.supplier_quoted_price !== undefined && s.supplier_quoted_price !== null)
          ? s.supplier_quoted_price
          : (s.supplier_submitted_price || undefined),
      })),
    };
    setSelectedReq(enriched);
    setPanelOpen(true);
  };

  const previewPdf = async (reqId) => {
    try {
      const res = await api.get(`/booking-requests/${reqId}/preview-pdf?lang=it`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error('Errore generazione anteprima PDF');
    }
  };

  const doAction = async (reqId, action, extraBody = {}) => {
    setActionLoading(action);
    try {
      let res;
      if (action === 'quote') {
        // Include supplier quoted prices if set
        const quotedPrices = {};
        (selectedReq?.services_detail || []).forEach(s => {
          if (s.is_on_request && s.supplier_quoted_price > 0) {
            quotedPrices[s.service_id] = s.supplier_quoted_price;
          }
        });
        res = await api.post(`/booking-requests/${reqId}/quote`, { lang: 'it', supplier_quoted_prices: quotedPrices });
        toast.success('Preventivo inviato via email');
      } else if (action === 'confirm') {
        res = await api.post(`/booking-requests/${reqId}/confirm`);
        toast.success('Prenotazione confermata — chiave inviata');
      } else if (action === 'reject') {
        const reason = prompt('Motivo rifiuto (opzionale):') || '';
        res = await api.post(`/booking-requests/${reqId}/reject`, { reason });
        toast.success('Richiesta rifiutata');
      } else if (action === 'pause') {
        res = await api.put(`/booking-requests/${reqId}/status`, { status: 'paused' });
        toast.success('Richiesta messa in pausa');
      }
      if (res?.data) {
        setSelectedReq(res.data);
        setRequests(prev => prev.map(r => r.id === reqId ? res.data : r));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore azione');
    } finally {
      setActionLoading('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Gestione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Richieste di Prenotazione</h2>
        </div>
        <button onClick={load} className="btn-ghost p-2" data-testid="refresh-requests-btn">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['', 'pending', 'quoted', 'confirmed', 'paused', 'rejected', 'expired'].map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            data-testid={`filter-${s || 'all'}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-sm border transition-colors
              ${filterStatus === s
                ? 'border-[#c9a84c] text-[#c9a84c] bg-[#c9a84c]/10'
                : 'border-[#222] text-[var(--lux-text-muted-3)] hover:border-[var(--lux-border-4)] hover:text-white'}`}
          >
            {s ? STATUS_LABELS[s] : 'Tutti'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div>
      ) : (
        <div className="card-dark rounded-lg overflow-hidden">
          {requests.length === 0 ? (
            <div className="p-16 text-center">
              <p className="text-[var(--lux-text-muted-3)] text-sm">Nessuna richiesta trovata.</p>
            </div>
          ) : (
            <table className="w-full table-dark">
              <thead>
                <tr>
                  <th className="text-left">Ospite</th>
                  <th className="text-left hidden md:table-cell">Date</th>
                  <th className="text-left hidden sm:table-cell">Notti</th>
                  <th className="text-right hidden md:table-cell">Totale</th>
                  <th className="text-left">Stato</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => {
                  const nights = req.checkin_date && req.checkout_date
                    ? Math.round((new Date(req.checkout_date) - new Date(req.checkin_date)) / 86400000) : '—';
                  return (
                    <tr key={req.id} className="cursor-pointer" onClick={() => openDetail(req)}>
                      <td>
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="text-white text-sm">{req.guest_name}</p>
                            <p className="text-[var(--lux-text-muted-3)] text-xs">{req.guest_email}</p>
                          </div>
                          {req.has_supplier_quotes && ['pending', 'paused'].includes(req.status) && (
                            <span
                              data-testid={`supplier-quote-badge-${req.id}`}
                              title="Preventivo fornitore disponibile"
                              className="flex items-center gap-1 text-[10px] text-amber-400 bg-amber-400/10 border border-amber-400/20 px-1.5 py-0.5 rounded shrink-0"
                            >
                              <Bell size={9} />
                              Fornitore
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="hidden md:table-cell text-[var(--lux-text-sec)] text-xs">
                        {req.checkin_date} → {req.checkout_date}
                      </td>
                      <td className="hidden sm:table-cell">{nights}</td>
                      <td className="text-right hidden md:table-cell">
                        <span className="font-serif text-[#c9a84c]">€{req.total_price?.toFixed(0)}</span>
                      </td>
                      <td>
                        <span className={`status-badge ${STATUS_COLORS[req.status] || ''}`}>
                          {STATUS_LABELS[req.status] || req.status}
                        </span>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1 justify-end">
                          {['pending', 'paused'].includes(req.status) && (
                            <button
                              onClick={() => doAction(req.id, 'quote')}
                              disabled={actionLoading === 'quote'}
                              className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-blue-400 transition-colors"
                              title="Invia preventivo"
                              data-testid={`quote-btn-${req.id}`}
                            >
                              <SendHorizonal size={14} />
                            </button>
                          )}
                          {['pending', 'quoted', 'paused'].includes(req.status) && (
                            <>
                              <button
                                onClick={() => doAction(req.id, 'confirm')}
                                disabled={actionLoading === 'confirm'}
                                className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-green-400 transition-colors"
                                title="Conferma"
                                data-testid={`confirm-btn-${req.id}`}
                              >
                                <Check size={14} />
                              </button>
                              <button
                                onClick={() => doAction(req.id, 'reject')}
                                className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-red-400 transition-colors"
                                title="Rifiuta"
                                data-testid={`reject-btn-${req.id}`}
                              >
                                <X size={14} />
                              </button>
                              <button
                                onClick={() => doAction(req.id, 'pause')}
                                className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-yellow-400 transition-colors"
                                title="Pausa"
                                data-testid={`pause-btn-${req.id}`}
                              >
                                <Pause size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Detail panel */}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent
          className="bg-[var(--lux-surface-1)] border-l border-[#222] text-white w-full sm:max-w-lg overflow-y-auto"
          data-testid="request-detail-panel"
        >
          {selectedReq && (
            <>
              <SheetHeader className="mb-6">
                <SheetTitle className="font-serif text-2xl font-light text-white">
                  {selectedReq.guest_name}
                </SheetTitle>
                <span className={`status-badge w-fit ${STATUS_COLORS[selectedReq.status] || ''}`}>
                  {STATUS_LABELS[selectedReq.status] || selectedReq.status}
                </span>
              </SheetHeader>
              <div className="space-y-6">
                <div>
                  <p className="label-gold text-xs mb-3">Informazioni Ospite</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-[var(--lux-text-muted-2)]">Email</span><span>{selectedReq.guest_email}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--lux-text-muted-2)]">Telefono</span><span>{selectedReq.guest_phone || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--lux-text-muted-2)]">Ospiti</span><span>{selectedReq.num_guests}</span></div>
                  </div>
                </div>
                <div>
                  <p className="label-gold text-xs mb-3">Date Soggiorno</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-[var(--lux-text-muted-2)]">Check-in</span><span>{selectedReq.checkin_date}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--lux-text-muted-2)]">Check-out</span><span>{selectedReq.checkout_date}</span></div>
                  </div>
                </div>
                <div>
                  <p className="label-gold text-xs mb-3">Prezzo</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-[var(--lux-text-muted-2)]">Alloggio</span><span>€{selectedReq.base_price?.toFixed(2)}</span></div>
                    {selectedReq.services_total > 0 && <div className="flex justify-between"><span className="text-[var(--lux-text-muted-2)]">Servizi (fissi)</span><span>€{selectedReq.services_total?.toFixed(2)}</span></div>}
                    {selectedReq.extras_total > 0 && <div className="flex justify-between"><span className="text-[var(--lux-text-muted-2)]">Extra</span><span>€{selectedReq.extras_total?.toFixed(2)}</span></div>}
                    {selectedReq.tax_amount > 0 && <div className="flex justify-between"><span className="text-[var(--lux-text-muted-2)]">Tassa soggiorno</span><span>€{selectedReq.tax_amount?.toFixed(2)}</span></div>}
                    {/* On-request services quoted total */}
                    {selectedReq.services_detail?.some(s => s.is_on_request && s.supplier_quoted_price > 0) && (
                      <div className="flex justify-between">
                        <span className="text-[var(--lux-text-muted-2)]">Servizi su richiesta</span>
                        <span>€{selectedReq.services_detail.filter(s => s.is_on_request).reduce((a, s) => a + (s.supplier_quoted_price || 0), 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-[#222] pt-2">
                      <span className="font-medium text-white">Totale</span>
                      <span className="font-serif text-[#c9a84c] text-xl">
                        €{((selectedReq.total_price || 0) + (selectedReq.services_detail?.filter(s => s.is_on_request).reduce((a, s) => a + (s.supplier_quoted_price || 0), 0) || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Services detail with rental/meal info and on-request price editing */}
                {selectedReq.services_detail?.length > 0 && (
                  <div>
                    <p className="label-gold text-xs mb-3">Servizi Richiesti</p>
                    <div className="border border-[var(--lux-border-1)] rounded-lg overflow-hidden">
                      {selectedReq.services_detail.map((s, i) => (
                        <div key={i} className="px-4 py-3 border-b border-[var(--lux-surface-2)] last:border-0">
                          <div className="flex justify-between items-start">
                            <p className="text-white text-sm">{s.service_name}</p>
                            {s.is_on_request ? (
                              <span className="text-amber-400 text-xs bg-amber-400/10 px-2 py-0.5 rounded shrink-0 ml-2">Su richiesta</span>
                            ) : (
                              <span className="text-[#c9a84c] text-xs shrink-0 ml-2">€{s.subtotal?.toFixed(2)}</span>
                            )}
                          </div>
                          {/* Meal plan */}
                          {(s.meal_breakfast || s.meal_lunch || s.meal_dinner) && (
                            <p className="text-[var(--lux-text-muted-3)] text-xs mt-1">
                              {[s.meal_breakfast && 'Colazione', s.meal_lunch && 'Pranzo', s.meal_dinner && 'Cena'].filter(Boolean).join(' · ')}
                            </p>
                          )}
                          {/* Rental variant */}
                          {s.selected_variant_name && (
                            <p className="text-[var(--lux-text-muted-2)] text-xs mt-1">Variante: <span className="text-[var(--lux-text-muted)]">{s.selected_variant_name}</span></p>
                          )}
                          {s.custom_field_values && Object.keys(s.custom_field_values).length > 0 && (
                            <div className="mt-1">
                              {Object.entries(s.custom_field_values).map(([label, value]) => (
                                <p key={label} className="text-[var(--lux-text-muted-3)] text-xs">{label}: <span className="text-[var(--lux-text-muted)]">{value}</span></p>
                              ))}
                            </div>
                          )}
                          {/* Admin: editable supplier quoted price */}
                          {s.is_on_request && ['pending', 'paused'].includes(selectedReq.status) && (
                            <div className="mt-2 space-y-1">
                              {s.supplier_submitted_price > 0 && (
                                <p className="text-[var(--lux-text-muted-2)] text-xs">
                                  Offerta fornitore:{' '}
                                  <span className="text-amber-400 font-medium">€{Number(s.supplier_submitted_price).toFixed(2)}</span>
                                  {s.supplier_submitted_at && (
                                    <span className="text-[var(--lux-text-muted-3)] ml-1">({new Date(s.supplier_submitted_at).toLocaleDateString('it-IT')})</span>
                                  )}
                                </p>
                              )}
                              <div className="flex items-center gap-2">
                                <span className="text-[var(--lux-text-muted-3)] text-xs whitespace-nowrap">Costo fornitore €:</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="input-dark text-xs py-1 px-2 w-24"
                                  placeholder="0.00"
                                  value={s.supplier_quoted_price ?? ''}
                                  onChange={e => {
                                    const price = parseFloat(e.target.value) || 0;
                                    setSelectedReq(prev => ({
                                      ...prev,
                                      services_detail: prev.services_detail.map((svc, j) => j === i ? { ...svc, supplier_quoted_price: price } : svc)
                                    }));
                                  }}
                                  data-testid={`supplier-price-${i}`}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedReq.special_requests && (
                  <div>
                    <p className="label-gold text-xs mb-2">Richieste Speciali</p>
                    <p className="text-[var(--lux-text-sec)] text-sm">{selectedReq.special_requests}</p>
                  </div>
                )}
                {/* Actions */}
                <div className="flex gap-3 flex-wrap pt-4 border-t border-[#222]">
                  {/* PDF preview — always available */}
                  <button
                    onClick={() => previewPdf(selectedReq.id)}
                    className="btn-ghost flex items-center gap-2 text-xs border border-[var(--lux-border-3)] hover:border-[var(--lux-text-muted-3)] px-4 py-2"
                    data-testid="panel-preview-pdf-btn"
                    title="Anteprima PDF preventivo"
                  >
                    <FileText size={12} />
                    Anteprima PDF
                  </button>
                  {['pending', 'paused'].includes(selectedReq.status) && (
                    <button onClick={() => doAction(selectedReq.id, 'quote')} disabled={actionLoading === 'quote'}
                      className="btn-outline-gold flex items-center gap-2 text-xs" data-testid="panel-quote-btn">
                      {actionLoading === 'quote' ? <Loader2 size={12} className="animate-spin" /> : <SendHorizonal size={12} />}
                      Invia Preventivo
                    </button>
                  )}
                  {['pending', 'quoted', 'paused'].includes(selectedReq.status) && (
                    <button onClick={() => doAction(selectedReq.id, 'confirm')} disabled={actionLoading === 'confirm'}
                      className="btn-gold flex items-center gap-2 text-xs" data-testid="panel-confirm-btn">
                      {actionLoading === 'confirm' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                      Conferma
                    </button>
                  )}
                  {['pending', 'quoted', 'paused'].includes(selectedReq.status) && (
                    <button onClick={() => doAction(selectedReq.id, 'reject')}
                      className="px-4 py-2 border border-red-800 text-red-400 text-xs uppercase tracking-widest hover:bg-red-900/20 transition-colors"
                      data-testid="panel-reject-btn">
                      Rifiuta
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
